import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Conversation } from "../models/Conversation";
import { ConversationService } from "../services/ConversationService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { MatterService } from "../services/MatterService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";
import { Message } from "../models/Message";
import { MessageService } from "../services/MessageService";
import { User } from "../models/User";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface ConversationContextProps {
  conversations: Conversation[];
  messages: Message[];
  selectedConversation: Conversation | null;
  setSelectedConversation: (conversationId: string) => void;
  createConversation: (conversationData: {
    subject: string;
    userIds: User["_id"][];
  }) => Promise<string>;
  createMessageForConversation: (
    messageData: Omit<
      Message,
      "_id" | "createdAt" | "updatedAt" | "createdById" | "updatedById"
    >,
  ) => Promise<string>;
  updateConversation: (
    conversationId: string,
    updatedData: Partial<
      Omit<Conversation, "_id" | "createdAt" | "updatedAt" | "updatedById">
    >,
  ) => Promise<void>;
  deleteConversationById: (conversationId: string) => void;
  loading: Loadings;
  error: Errors;
}

const ConversationContext = createContext<ConversationContextProps | undefined>(
  undefined,
);

export const useConversation = (): ConversationContextProps => {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error(
      "useConversation must be used within a ConversationProvider",
    );
  }
  return context;
};

export const ConversationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: false,
    details: false,
  });

  useSocketEventByIds("conversations:changed", ["*"], () => {
    fetchConversations();
  });

  useSocketEventByIds("messages:changed", ["*"], (fullDocument: Message) => {
    if (fullDocument.conversationId === selectedConversation?._id) {
      setMessages((prev) => [...prev, fullDocument]);
    }
  });

  const fetchConversations = async () => {
    if (!user || !currentMatterId) return;
    try {
      const list = await ConversationService.getAllConversations();
      setConversations(list);
      setErrors((prev) => ({ ...prev, list: null }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, list: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, list: false }));
    }
  };

  useEffect(() => {
    setLoadings((prev) => ({ ...prev, list: true }));
    fetchConversations();
  }, [user, currentMatterId]);

  const setSelectedConversationById = async (conversationId: string) => {
    if (!currentMatterId) return;
    try {
      setLoadings((prev) => ({ ...prev, details: true }));
      const conversationData =
        await ConversationService.getConversation(conversationId);
      const messages = await ConversationService.getMessages(conversationId);
      setSelectedConversation(conversationData);
      setMessages(messages);
      setErrors((prev) => ({ ...prev, details: null }));
    } catch (err: any) {
      setSelectedConversation(null);
      setErrors((prev) => ({ ...prev, details: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createConversation = async (conversationData: {
    subject: string;
    userIds: User["_id"][];
  }): Promise<string> => {
    const newId = await ConversationService.createConversation({
      ...conversationData,
      matterId: currentMatterId!,
    });
    return newId;
  };

  const createMessageForConversation = async (
    messageData: Omit<
      Message,
      "_id" | "createdAt" | "updatedAt" | "createdById" | "updatedById"
    >,
  ): Promise<string> => {
    const newMessageId = await MessageService.createMessage(messageData);
    return newMessageId;
  };

  const updateConversation = async (
    conversationId: string,
    updatedData: Partial<
      Omit<Conversation, "_id" | "createdAt" | "updatedAt" | "createdById">
    >,
  ) => {
    if (!currentMatterId) return;
    await ConversationService.updateConversation(conversationId, updatedData);
    setSelectedConversationById(conversationId);
  };

  const deleteConversationById = async (conversationId: string) => {
    await ConversationService.deleteConversation(conversationId);
    setConversations((prev) => prev.filter((p) => p._id !== conversationId));
    setSelectedConversation(null);
    setMessages([]);
  };

  return (
    <ConversationContext.Provider
      value={{
        conversations,
        messages,
        selectedConversation,
        setSelectedConversation: setSelectedConversationById,
        createConversation,
        updateConversation,
        deleteConversationById,
        createMessageForConversation,
        loading: loadings,
        error: errors,
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
};
